import { formatBrazilianPhone } from 'shared/helpers/talkviaInputMasks';
import { quoteInvoiceItems } from './quoteInvoiceItems';

// Builds the printable quote. The default layout is generated here; saved
// custom templates keep their own body and only receive escaped values.

const HTML_ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export const escapeQuoteHtml = value =>
  String(value ?? '').replace(
    /[&<>"']/g,
    character => HTML_ENTITIES[character]
  );

const text = value => String(value ?? '').trim();
const digitsOf = value => String(value ?? '').replace(/\D/g, '');

export const formatQuoteCurrency = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0)
  );

const formatQuantity = value =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 }).format(
    Number(value || 0)
  );

export const formatQuoteDocument = value => {
  const raw = text(value);
  const digits = digitsOf(raw);
  if (digits.length === 11) {
    return {
      label: 'CPF',
      value: digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'),
    };
  }
  if (digits.length === 14) {
    return {
      label: 'CNPJ',
      value: digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      ),
    };
  }
  return { label: 'CPF/CNPJ', value: raw };
};

export const formatQuotePhone = value => {
  const raw = text(value);
  const digits = digitsOf(raw);
  const isForeign = raw.startsWith('+') && !digits.startsWith('55');
  const isBrazilian =
    !isForeign &&
    (digits.length === 10 ||
      digits.length === 11 ||
      (digits.startsWith('55') && [12, 13].includes(digits.length)));
  return isBrazilian ? formatBrazilianPhone(digits) : raw;
};

const formatZipCode = value => {
  const digits = digitsOf(value);
  return digits.length === 8
    ? digits.replace(/(\d{5})(\d{3})/, '$1-$2')
    : text(value);
};

const toDate = value => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatQuoteDate = value => {
  const date = toDate(value);
  return date ? date.toLocaleDateString('pt-BR') : '';
};

export const formatQuoteDateTime = value => {
  const date = toDate(value);
  if (!date) return '';
  const time = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date.toLocaleDateString('pt-BR')} às ${time}`;
};

export const quoteAddressLines = address => {
  const source = address && typeof address === 'object' ? address : {};
  const street = [text(source.address), text(source.number)]
    .filter(Boolean)
    .join(', ');
  const cityState = [text(source.city), text(source.state)]
    .filter(Boolean)
    .join(' - ');
  const zipCode = text(source.zip_code)
    ? `CEP ${formatZipCode(source.zip_code)}`
    : '';
  return [
    [street, text(source.complement)].filter(Boolean).join(' - '),
    [text(source.neighborhood), cityState].filter(Boolean).join(', '),
    zipCode,
  ].filter(Boolean);
};

export const quoteCompanyInfo = account => {
  const profile = account?.custom_attributes?.company_profile || {};
  const tradeName = text(profile.nome_fantasia) || text(account?.name);
  const legalName = text(profile.razao_social);
  const cnpj =
    digitsOf(profile.cnpj).length === 14
      ? formatQuoteDocument(profile.cnpj).value
      : '';
  const cityState = [text(profile.cidade), text(profile.estado)]
    .filter(Boolean)
    .join(' - ');
  return {
    name: tradeName || legalName,
    legalName: legalName && legalName !== tradeName ? legalName : '',
    cnpj,
    address: [
      text(profile.endereco),
      cityState,
      text(profile.cep) ? `CEP ${formatZipCode(profile.cep)}` : '',
    ]
      .filter(Boolean)
      .join(', '),
    phone: formatQuotePhone(profile.telefone),
    email: text(profile.email),
  };
};

const quoteNumber = payload =>
  text(payload?.quote_number || payload?.contract_id);

export const quoteDocumentTitle = payload =>
  ['Orçamento', text(payload?.recipient_name), quoteNumber(payload)]
    .filter(Boolean)
    .join(' - ');

const field = (label, value) =>
  value
    ? `<div class="field"><dt>${escapeQuoteHtml(label)}</dt><dd>${escapeQuoteHtml(value)}</dd></div>`
    : '';

const DEFAULT_STYLES = `
  @page { size: A4; margin: 14mm 14mm 16mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { color: #1f2937; background: #ffffff; font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; font-size: 11.5px; line-height: 1.5; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; }
  h1, h2, p, dl, dd { margin: 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 16px; border-bottom: 2px solid #1e3a5f; }
  .company-name { font-size: 18px; font-weight: 700; color: #111827; letter-spacing: -0.01em; }
  .company-meta { margin-top: 4px; color: #4b5563; font-size: 10.5px; }
  .company-meta span { display: block; }
  .doc-id { flex-shrink: 0; min-width: 44mm; text-align: right; }
  .doc-kind { display: inline-block; padding: 3px 10px; border-radius: 999px; background: #1e3a5f; color: #ffffff; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .doc-number { margin-top: 8px; font-size: 20px; font-weight: 700; color: #111827; }
  .doc-date { margin-top: 2px; color: #4b5563; font-size: 10.5px; }
  .intro { margin: 16px 0 14px; color: #374151; }
  .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
  .grid.single { grid-template-columns: minmax(0, 1fr); }
  .card { border: 1px solid #d9dee5; border-radius: 8px; padding: 12px 14px; break-inside: avoid; page-break-inside: avoid; }
  .card-title { margin-bottom: 8px; color: #1e3a5f; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .field { display: grid; grid-template-columns: 22mm minmax(0, 1fr); gap: 8px; padding: 2px 0; }
  .field dt { color: #6b7280; }
  .field dd { color: #111827; font-weight: 600; overflow-wrap: anywhere; }
  .section-title { margin: 0 0 8px; color: #1e3a5f; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  th { padding: 8px 10px; background: #eef2f6; color: #374151; font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-align: left; text-transform: uppercase; border-bottom: 1px solid #cfd6df; }
  td { padding: 9px 10px; border-bottom: 1px solid #e5e9ef; vertical-align: top; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .index { width: 8mm; color: #6b7280; }
  .item-name { font-weight: 600; color: #111827; overflow-wrap: anywhere; }
  .empty { padding: 14px; color: #6b7280; text-align: center; }
  .closing { display: grid; grid-template-columns: minmax(0, 1fr) 70mm; gap: 12px; align-items: start; break-inside: avoid; page-break-inside: avoid; }
  .totals { border: 1px solid #d9dee5; border-radius: 8px; overflow: hidden; }
  .totals-row { display: flex; justify-content: space-between; gap: 12px; padding: 8px 14px; color: #4b5563; border-bottom: 1px solid #e5e9ef; }
  .totals-row strong { color: #111827; font-variant-numeric: tabular-nums; }
  .totals-total { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 12px 14px; background: #1e3a5f; color: #ffffff; }
  .totals-total span { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .totals-total strong { font-size: 17px; font-variant-numeric: tabular-nums; }
  .next-steps { margin-top: 16px; padding: 12px 14px; border-left: 3px solid #1e3a5f; background: #f5f7fa; border-radius: 0 8px 8px 0; color: #374151; break-inside: avoid; page-break-inside: avoid; }
  .next-steps strong { display: block; margin-bottom: 4px; color: #111827; }
  .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #d9dee5; color: #6b7280; font-size: 9.5px; display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
`;

const itemRows = items =>
  items
    .map(
      (item, index) =>
        `<tr><td class="index">${index + 1}</td><td class="item-name">${escapeQuoteHtml(item.product_name || 'Item')}</td><td class="num">${formatQuantity(item.quantity)}</td><td class="num">${formatQuoteCurrency(item.unit_price)}</td><td class="num"><strong>${formatQuoteCurrency(item.total)}</strong></td></tr>`
    )
    .join('');

const companyHeader = company => {
  const meta = [
    company.legalName,
    company.cnpj ? `CNPJ ${company.cnpj}` : '',
    company.address,
    [company.phone, company.email].filter(Boolean).join(' · '),
  ].filter(Boolean);
  return `<div class="company">${company.name ? `<div class="company-name">${escapeQuoteHtml(company.name)}</div>` : ''}${meta.length ? `<div class="company-meta">${meta.map(line => `<span>${escapeQuoteHtml(line)}</span>`).join('')}</div>` : ''}</div>`;
};

export const buildDefaultQuoteDocument = ({
  payload = {},
  account = null,
  issuedAt = new Date(),
} = {}) => {
  const company = quoteCompanyInfo(account);
  const items = quoteInvoiceItems(payload);
  const total = items.reduce((sum, item) => sum + Number(item.total || 0), 0);
  const number = quoteNumber(payload);
  const documentInfo = formatQuoteDocument(payload.recipient_cpf);
  const customerName = text(payload.recipient_name);
  const address = quoteAddressLines(payload.recipient_address).join(', ');
  const departure = formatQuoteDateTime(payload.scheduled_at);
  const returnAt = formatQuoteDateTime(payload.pickup_at);
  const paymentMethod = text(payload.payment_method);
  const issuedDate = formatQuoteDate(issuedAt);

  const customerCard = `<div class="card"><h2 class="card-title">Cliente</h2><dl>${field('Nome', customerName || 'Não informado')}${field(documentInfo.label, documentInfo.value)}${field('Telefone', formatQuotePhone(payload.recipient_phone))}${field('Endereço', address)}</dl></div>`;
  const periodCard =
    departure || returnAt
      ? `<div class="card"><h2 class="card-title">Período</h2><dl>${field('Saída', departure)}${field('Retorno', returnAt)}</dl></div>`
      : '';
  const paymentCard = paymentMethod
    ? `<div class="card"><h2 class="card-title">Forma de pagamento</h2><p>${escapeQuoteHtml(paymentMethod)}</p></div>`
    : '<div></div>';
  const itemCount = `${items.length} ${items.length === 1 ? 'item' : 'itens'}`;

  const table = `<table><thead><tr><th class="index">#</th><th>Descrição</th><th class="num">Qtd</th><th class="num">Valor unitário</th><th class="num">Total</th></tr></thead><tbody>${itemRows(items) || '<tr><td class="empty" colspan="5">Nenhum item informado.</td></tr>'}</tbody></table>`;

  const greeting = customerName
    ? `Olá, ${escapeQuoteHtml(customerName)}! Preparamos este orçamento com os itens e as condições abaixo.`
    : 'Preparamos este orçamento com os itens e as condições abaixo.';

  const footer = [
    company.name ? escapeQuoteHtml(company.name) : '',
    `Orçamento${number ? ` nº ${escapeQuoteHtml(number)}` : ''} · emitido em ${escapeQuoteHtml(issuedDate)}`,
  ]
    .filter(Boolean)
    .map(line => `<span>${line}</span>`)
    .join('');

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeQuoteHtml(quoteDocumentTitle(payload))}</title><style>${DEFAULT_STYLES}</style></head><body><main class="page">
<header class="header">${companyHeader(company)}<div class="doc-id"><span class="doc-kind">Orçamento</span>${number ? `<div class="doc-number">Nº ${escapeQuoteHtml(number)}</div>` : ''}<div class="doc-date">Emitido em ${escapeQuoteHtml(issuedDate)}</div></div></header>
<p class="intro">${greeting}</p>
<section class="grid${periodCard ? '' : ' single'}">${customerCard}${periodCard}</section>
<section><h2 class="section-title">Itens do orçamento</h2>${table}</section>
<section class="closing">${paymentCard}<div class="totals"><div class="totals-row"><span>Subtotal (${itemCount})</span><strong>${formatQuoteCurrency(total)}</strong></div><div class="totals-total"><span>Total</span><strong>${formatQuoteCurrency(total)}</strong></div></div></section>
<section class="next-steps"><strong>Próximos passos</strong>Para aprovar este orçamento, ajustar algum item ou tirar dúvidas, responda à nossa mensagem ou fale com a nossa equipe. Este documento é uma proposta: ele não comprova pagamento nem confirma a reserva dos itens.</section>
<footer class="footer">${footer}</footer>
</main></body></html>`;
};

// Placeholders that already contain escaped HTML built by the system.
const HTML_PLACEHOLDERS = new Set(['produtos_lista', 'produtos_tabela']);

export const renderQuoteTemplateBody = (body, values) =>
  String(body ?? '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => {
    const value = values[key];
    if (value === undefined || value === null) return '';
    return HTML_PLACEHOLDERS.has(key) ? String(value) : escapeQuoteHtml(value);
  });

const CUSTOM_STYLES = `
  @page { size: A4; margin: 14mm; }
  body { margin: 0; color: #111827; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.45; }
  .page { width: 100%; }
  p { margin: 0 0 10px; }
  strong { font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  th, td { border: 1px solid #111827; padding: 8px; vertical-align: top; text-align: left; }
  th { font-weight: 700; background: #f3f4f6; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

export const buildCustomQuoteDocument = ({
  payload = {},
  bodyHtml = '',
} = {}) =>
  `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeQuoteHtml(quoteDocumentTitle(payload))}</title><style>${CUSTOM_STYLES}</style></head><body><main class="page">${bodyHtml}</main></body></html>`;
